/**
 * FILE: assets/custom-metafield-search.js
 *
 * IMPORTANT: This version does NOT replace or modify Shopify's native
 * predictive search. Your original snippets/predictive-search.liquid
 * and sections/predictive-search.liquid should be left EXACTLY as they
 * came with the theme (revert them if you changed them earlier).
 *
 * This script runs ALONGSIDE the native search. It listens to the same
 * search input, fetches your metafield JSON feed (see
 * templates/page.search-index.liquid), and injects an EXTRA results
 * block (labelled "More matches") right after the search form —
 * showing products that match on Composer, Instrumentation, ISBN13,
 * UPC, Tension, Colour, etc. It never touches or overwrites the native
 * results container, so it can't break anything the theme's own JS
 * depends on.
 *
 * SETUP:
 * 1. Add this file as assets/custom-metafield-search.js
 * 2. Add assets/custom-metafield-search.css
 * 3. In theme.liquid (just before </body>, or anywhere loaded on every
 *    page), add:
 *      <script src="{{ 'custom-metafield-search.js' | asset_url }}" defer="defer"></script>
 *      {{ 'custom-metafield-search.css' | asset_url | stylesheet_tag }}
 *    (Do NOT edit predictive-search.liquid for this.)
 * 4. Make sure templates/page.search-index.liquid exists and its
 *    metafield namespace.key values match your ACTUAL metafield
 *    definitions (Settings > Custom data > Products).
 */
(function () {
  "use strict";

  var FEED_URL = "/pages/search-index";
  var CACHE_KEY = "cc_metafield_search_index_v1";
  var CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour
  var MIN_QUERY_LENGTH = 2;
  var MAX_RESULTS = 6;
  var DEBOUNCE_MS = 200;

  // Only match on these metafields (title matches are already handled
  // by Shopify's native search, so we skip title here to avoid dupes).
  var METAFIELD_LABELS = {
    instrument: "Instrumentation",
    composer: "Composer",
    arranger: "Arranger",
    contents_list: "Contents",
    isbn13: "ISBN13",
    upc: "UPC",
    supplier_code: "Supplier Code",
    label: "Label",
    tension: "Tension",
    colour: "Colour",
    size: "Size",
    made_in: "Made In"
  };
  var METAFIELD_KEYS = Object.keys(METAFIELD_LABELS);

  var fetchPromise = null;

  function readCache() {
    try {
      var raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || !parsed.timestamp || !parsed.products) return null;
      if (Date.now() - parsed.timestamp > CACHE_TTL_MS) return null;
      return parsed.products;
    } catch (e) {
      return null;
    }
  }

  function writeCache(products) {
    try {
      localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({ timestamp: Date.now(), products: products })
      );
    } catch (e) {}
  }

  function fetchPage(pageNum) {
    return fetch(FEED_URL + "?page=" + pageNum, {
      headers: { Accept: "application/json" }
    }).then(function (res) {
      if (!res.ok) throw new Error("Search index fetch failed: " + res.status);
      return res.json();
    });
  }

  function fetchAllProducts() {
    if (fetchPromise) return fetchPromise;

    var cached = readCache();
    if (cached) {
      fetchPromise = Promise.resolve(cached);
      return fetchPromise;
    }

    fetchPromise = fetchPage(1).then(function (first) {
      var all = first.products || [];
      var totalPages = first.pages || 1;

      if (totalPages <= 1) {
        writeCache(all);
        return all;
      }

      var pagePromises = [];
      for (var p = 2; p <= totalPages; p++) {
        pagePromises.push(fetchPage(p));
      }

      return Promise.all(pagePromises).then(function (rest) {
        rest.forEach(function (pageData) {
          all = all.concat(pageData.products || []);
        });
        writeCache(all);
        return all;
      });
    });

    return fetchPromise;
  }

  function normalise(str) {
    return (str || "").toString().toLowerCase().trim();
  }

  function matchProduct(product, q) {
    for (var i = 0; i < METAFIELD_KEYS.length; i++) {
      var key = METAFIELD_KEYS[i];
      var val = normalise(product[key]);
      if (val && val.indexOf(q) !== -1) {
        return key;
      }
    }
    return null;
  }

  function searchMetafields(query, products) {
    var q = normalise(query);
    if (q.length < MIN_QUERY_LENGTH) return [];

    var results = [];
    for (var i = 0; i < products.length; i++) {
      var field = matchProduct(products[i], q);
      if (field) {
        results.push({ product: products[i], matchedField: field });
      }
    }
    return results.slice(0, MAX_RESULTS);
  }

  function escapeHtml(str) {
    var div = document.createElement("div");
    div.textContent = str == null ? "" : String(str);
    return div.innerHTML;
  }

  function renderBlock(container, results, query) {
    if (!results.length) {
      container.hidden = true;
      container.innerHTML = "";
      return;
    }

    var html =
      '<div class="cms-metafield-heading">More matches for &ldquo;' +
      escapeHtml(query) +
      "&rdquo;</div>";
    html += '<ul class="cms-results-list" role="listbox">';

    results.forEach(function (r) {
      var p = r.product;
      var label = METAFIELD_LABELS[r.matchedField] || r.matchedField;
      html +=
        '<li class="cms-result-item" role="option">' +
        '<a href="' +
        escapeHtml(p.url) +
        '" class="cms-result-link">' +
        (p.image
          ? '<img class="cms-result-image" src="' +
            escapeHtml(p.image) +
            '" alt="" loading="lazy" width="48" height="48">'
          : '<span class="cms-result-image cms-result-image--empty"></span>') +
        '<span class="cms-result-text">' +
        '<span class="cms-result-title">' +
        escapeHtml(p.title) +
        "</span>" +
        '<span class="cms-result-match">' +
        escapeHtml(label) +
        ": " +
        escapeHtml(p[r.matchedField]) +
        "</span>" +
        "</span>" +
        "</a>" +
        "</li>";
    });

    html += "</ul>";
    container.innerHTML = html;
    container.hidden = false;
  }

  function debounce(fn, wait) {
    var timer = null;
    return function () {
      var args = arguments;
      var ctx = this;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  function ensureContainer(input) {
    var existingId = input.getAttribute("data-cms-container-id");
    if (existingId) {
      var existing = document.getElementById(existingId);
      if (existing) return existing;
    }

    var form = input.closest("form");
    var anchor = form || input;
    var id = "cms-metafield-results-" + Math.random().toString(36).slice(2, 9);

    var container = document.createElement("div");
    container.id = id;
    container.className = "cms-metafield-results";
    container.hidden = true;

    // Insert right after the form (below the native predictive
    // search dropdown) without touching that dropdown's own markup.
    anchor.insertAdjacentElement("afterend", container);
    input.setAttribute("data-cms-container-id", id);

    return container;
  }

  function initInput(input) {
    var container = ensureContainer(input);

    input.addEventListener(
      "focus",
      function () {
        fetchAllProducts();
      },
      { once: true }
    );

    var handleInput = debounce(function () {
      var query = input.value;

      if (normalise(query).length < MIN_QUERY_LENGTH) {
        container.hidden = true;
        container.innerHTML = "";
        return;
      }

      fetchAllProducts()
        .then(function (products) {
          var results = searchMetafields(query, products);
          renderBlock(container, results, query);
        })
        .catch(function () {
          container.hidden = true;
        });
    }, DEBOUNCE_MS);

    input.addEventListener("input", handleInput);

    document.addEventListener("click", function (e) {
      if (e.target !== input && !container.contains(e.target)) {
        container.hidden = true;
      }
    });
  }

  function init() {
    var inputs = document.querySelectorAll(".js-search-input");
    inputs.forEach(function (input) {
      initInput(input);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();